// BioForge 2.2 — Pitch Deck full-screen, varied per-slide layouts.
import { ventureHeroFor } from "@/scenario_images";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronLeft, ChevronRight, X, ArrowRight, Stethoscope, Maximize2, Minimize2,
} from "lucide-react";
import { useProject, formatUSD } from "@/lib";
import { TechConvergence, RiskMatrix, MarketFunnel } from "@/viz";

export const FEATURED_RUN_ID = "fc526bce-d199-433b-8475-8ba28829527f";

export function PitchDeck() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presentMode = searchParams.get("present") === "1";
  const { doc, loading } = useProject(id, { pollIfRunning: true });
  const [idx, setIdx] = useState(0);
  const [isFull, setIsFull] = useState(false);
  const containerRef = useRef(null);

  // Add body class so global CSS can hide the Emergent badge etc. while presenting.
  useEffect(() => {
    document.body.classList.add("pitch-mode");
    // Directly override inline-style !important on the platform badge.
    const badge = document.getElementById("emergent-badge");
    const prevDisplay = badge ? badge.style.display : null;
    if (badge) badge.style.setProperty("display", "none", "important");
    return () => {
      document.body.classList.remove("pitch-mode");
      if (badge) {
        if (prevDisplay) badge.style.setProperty("display", prevDisplay, "important");
        else badge.style.removeProperty("display");
      }
    };
  }, []);

  const fin = doc?.final_report;
  const com = doc?.outputs?.commercialization_agent;
  const fr = doc?.founder_review;
  const ia = doc?.outputs?.innovation_architect;
  const reg = fin?.regulatory_strategy || doc?.outputs?.regulatory_planner;
  const trial = doc?.outputs?.clinical_trial_designer;
  const pds = fin?.pitch_deck_summary || {};

  const slides = fin ? buildSlides({ pds, fin, com, fr, ia, reg, trial, problem: doc?.inputs?.problem, runId: doc?.run_id }) : [];
  const total = slides.length;
  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);

  // Fullscreen handlers
  const requestFs = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };
  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ?present=1 → auto-enter browser fullscreen on mount.
  useEffect(() => {
    if (!presentMode) return;
    const t = setTimeout(() => {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [presentMode]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(-1); }
      else if (e.key === "f" || e.key === "F") { e.preventDefault(); requestFs(); }
      else if (e.key === "Escape" && !document.fullscreenElement) { navigate(`/projects/${id}/overview`); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, id, navigate]);

  if (loading) return <Skeleton className="h-screen w-full" />;
  if (!fin) return <div className="p-12 text-center text-sm text-white/40 italic">Pitch deck unlocks once the venture report is generated.</div>;

  const slide = slides[idx];

  return (
    <div ref={containerRef} className="h-screen w-full bg-[#0A0B0E] flex flex-col" data-testid="pitch-deck">
      {/* Topbar */}
      <header className="shrink-0 px-6 py-3 border-b border-white/[0.04] flex items-center justify-between bg-black/30 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-md border border-teal-400/30 bg-teal-400/10 flex items-center justify-center">
            <Stethoscope className="w-3.5 h-3.5 text-teal-300" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">{fin.company_name}</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/40">Generated by Aignoz Innovation OS</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">{slide?.title}</div>
        <div className="flex items-center gap-2">
          <span data-testid="slide-counter" className="text-xs mono text-white/40">{idx + 1} / {total}</span>
          <Button size="sm" variant="outline" onClick={requestFs} data-testid="present-btn">
            {isFull ? <Minimize2 className="w-3 h-3 mr-1.5" /> : <Maximize2 className="w-3 h-3 mr-1.5" />}
            {isFull ? "Exit" : "Present"}
          </Button>
          {!isFull && (
            <button onClick={() => navigate(`/projects/${id}/overview`)} className="text-white/40 hover:text-white/80 transition" data-testid="pitch-close">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      {/* Slide canvas */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-12 py-8 overflow-hidden relative">
        <AnimatePresence mode="wait">
          <motion.div key={idx}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.25, 1] }}
            className="w-full max-w-6xl">
            {slide?.render()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom controls */}
      <footer className="shrink-0 px-6 py-3 border-t border-white/[0.04] flex items-center justify-between bg-black/20">
        <Button variant="outline" size="sm" onClick={() => go(-1)} disabled={idx === 0} data-testid="slide-prev">
          <ChevronLeft className="w-3 h-3 mr-1" />Prev
        </Button>
        <div className="flex gap-1.5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`slide-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8" : "bg-white/15 hover:bg-white/30 w-3"}`}
              style={i === idx ? { backgroundColor: "#D17EC0", boxShadow: "0 0 10px rgba(209,126,192,0.55)" } : {}}
            />
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => go(1)} disabled={idx === total - 1} data-testid="slide-next">
          Next<ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </footer>
    </div>
  );
}

function buildSlides({ pds, fin, com, fr, ia, reg, trial, problem, runId }) {
  const hero = ventureHeroFor(runId);
  return [
    // 1. Problem — full-bleed hero for showcase ventures
    {
      title: "Problem",
      render: () => hero ? (
        <div className="absolute inset-0">
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(135deg, rgba(10,11,14,0.92) 0%, rgba(10,11,14,0.75) 45%, rgba(10,11,14,0.30) 100%)",
          }} />
          <div className="absolute inset-0 flex items-center px-12 lg:px-20">
            <div className="max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/90 mb-3">Slide 1 · The Problem</div>
              <div className="text-3xl md:text-5xl leading-tight font-semibold text-white">{pds.problem}</div>
              {fin.company_name && (
                <div className="text-base text-white/65 mt-6 max-w-xl">{fin.company_name}</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <SlideLayout kicker="Slide 1" title="The Problem">
          <div className="text-3xl md:text-4xl leading-relaxed max-w-4xl">{pds.problem}</div>
        </SlideLayout>
      ),
    },
    // 2. Solution — left text + right numbered concepts
    {
      title: "Solution",
      render: () => (
        <SlideLayout kicker="Slide 2" title="The Solution">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-7"><div className="text-2xl leading-relaxed">{pds.solution}</div></div>
            <div className="col-span-5 space-y-3">
              {(ia?.concepts || []).slice(0, 3).map((c, i) => (
                <div key={i} className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/70">Concept {i + 1}</div>
                  <div className="text-base font-semibold mt-1">{c.name}</div>
                  <div className="text-xs text-white/65 mt-1 line-clamp-3">{c.differentiator}</div>
                </div>
              ))}
            </div>
          </div>
        </SlideLayout>
      ),
    },
    // 3. Technology — full-bleed convergence
    {
      title: "Technology",
      render: () => (
        <SlideLayout kicker="Slide 3" title="Technology Convergence">
          <TechConvergence problem={problem} technologies={fin.technology_stack} companyName={fin.company_name} large />
          <div className="mt-8 max-w-3xl text-lg text-white/75 leading-relaxed">{pds.technology}</div>
        </SlideLayout>
      ),
    },
    // 4. Validation — left timeline pill + right details
    {
      title: "Validation",
      render: () => (
        <SlideLayout kicker="Slide 4" title="Validation">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-7"><div className="text-xl leading-relaxed">{pds.validation_plan}</div></div>
            <div className="col-span-5 space-y-3">
              <Big label="Timeline" value={`${trial?.timeline_months || "—"} mo`} />
              <Big label="Sample size" value={trial?.sample_size_estimate?.toLocaleString?.() || trial?.sample_size_estimate || "—"} />
              <Big label="Primary endpoint" value={trial?.primary_endpoint?.slice(0, 60) || "—"} small />
            </div>
          </div>
        </SlideLayout>
      ),
    },
    // 5. Regulatory — three columns
    {
      title: "Regulatory",
      render: () => (
        <SlideLayout kicker="Slide 5" title="Regulatory Pathway">
          <div className="text-lg text-white/75 leading-relaxed mb-6 max-w-3xl">{pds.regulatory_path}</div>
          <div className="grid grid-cols-3 gap-4">
            {[["CDSCO", reg?.india_cdsco_pathway], ["FDA", reg?.fda_pathway], ["CE", reg?.ce_pathway]].map(([t, txt]) => (
              <div key={t} className="rounded-lg border border-teal-400/20 bg-teal-400/[0.04] p-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-2">{t}</div>
                <div className="text-sm leading-relaxed text-white/85 line-clamp-5">{txt || "Pending"}</div>
              </div>
            ))}
          </div>
        </SlideLayout>
      ),
    },
    // 6. Market — funnel centerpiece + caption
    {
      title: "Market",
      render: () => (
        <SlideLayout kicker="Slide 6" title="Market Opportunity">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-7 flex justify-center"><MarketFunnel tam={com?.tam_usd} sam={com?.sam_usd} som={com?.som_usd} large /></div>
            <div className="col-span-5">
              <div className="text-lg leading-relaxed text-white/75">{pds.market_opportunity}</div>
            </div>
          </div>
        </SlideLayout>
      ),
    },
    // 7. Business Model — body left, partners right
    {
      title: "Business Model",
      render: () => (
        <SlideLayout kicker="Slide 7" title="Business Model">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-7 text-xl leading-relaxed">{pds.business_model}</div>
            <div className="col-span-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Pilot partners</div>
              <div className="flex flex-wrap gap-1.5">{(com?.pilot_partners || []).map((p, i) => (
                <span key={i} className="text-[11px] px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08]">{p}</span>
              ))}</div>
            </div>
          </div>
        </SlideLayout>
      ),
    },
    // 8. Risks — Risk Matrix centerpiece + top 3
    {
      title: "Risks",
      render: () => (
        <SlideLayout kicker="Slide 8" title="Risk Matrix">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8"><RiskMatrix fr={fr} /></div>
            <div className="col-span-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-3">Top 3 mitigations</div>
              <ol className="space-y-3">
                {(fr?.top_3_improvements || []).map((s, i) => (
                  <li key={i} className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full border border-emerald-400/40 text-emerald-300 text-xs flex items-center justify-center font-semibold">{i + 1}</span><span className="text-sm leading-relaxed text-white/85">{s}</span></li>
                ))}
              </ol>
            </div>
          </div>
        </SlideLayout>
      ),
    },
    // 9. Why Now — single oversized quote
    {
      title: "Why Now",
      render: () => (
        <div className="flex flex-col items-center text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/70 mb-4">Slide 9 · Closing</div>
          <h2 className="text-5xl md:text-6xl font-semibold tracking-tight mb-8">Why Now</h2>
          <blockquote className="text-3xl md:text-4xl italic leading-snug text-teal-100 max-w-4xl">
            "{pds.why_now}"
          </blockquote>
          <div className="mt-10 text-[11px] uppercase tracking-[0.22em] text-white/40">— {fin.company_name}</div>
        </div>
      ),
    },
  ];
}

function SlideLayout({ kicker, title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-teal-300/70 mb-3">{kicker}</div>
      <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-8">{title}</h2>
      {children}
    </div>
  );
}

function Big({ label, value, small = false }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{label}</div>
      <div className={`mono font-semibold mt-1 ${small ? "text-base leading-tight" : "text-3xl"} text-teal-100`}>{value}</div>
    </div>
  );
}
